<template>
  <modal :open="internalOpen" :full-width="true" :full-height="true">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.images.photos.ratingModal.title') }}</h1>
      </modal-header>
    </template>

    <div class="pt-8 h-full">
      <div class="flex flex-col justify-center w-full h-full">
        <div v-if="loading" class="flex justify-center">
          <Spinner />
        </div>
        <div v-else class="grow">
          <div class="pb-3">
            {{
              $t('pages.images.photos.ratingModal.count', [
                currentIndex + 1,
                totalNumberOfPhotos,
              ], totalNumberOfPhotos)
            }}
          </div>
          <PhotoRating v-model:photo="photos[currentIndex]" v-model:loading-rated="loadingRated" @rated="next()" />
        </div>
        <div v-if="!loading && totalNumberOfPhotos > 1" class="flex justify-between w-full">
          <custom-button
            variant="text"
            class="mt-15"
            :disabled="loadingRated"
            @click="previous()"
          >
            {{ `< ${$t('back')}` }}
          </custom-button>
          <custom-button
            variant="text"
            class="mt-15"
            :disabled="loadingRated"
            @click="next()"
          >
            {{ `${$t('next')} >` }}
          </custom-button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, ref, unref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { type ProviderParams, useProvider } from '@utils/table/hooks/provider';
import Spinner from '@utils/Spinner.vue';
import PhotoRating from '@pages/images/photos/PhotoRating.vue';
import type { HydraCollection, Photo } from '@/entities/Entities';

interface Props {
  open: boolean;
  providerParams: ProviderParams;
  openOnPhotoId?: string;
}

const loadingRated = ref(false);

const emit = defineEmits(['update:open', 'close']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const currentIndex = ref(0);

const providerParams = computed(() => ({
  ...props.providerParams,
  filter: {
    ...props.providerParams.filter,
    pagination: false,
  },
}));

const photos = ref<Photo[]>([]);
const totalNumberOfPhotos = computed(() => photos.value.length);

const { data, loading, refetch } = useProvider<HydraCollection<Photo>>('/photos', providerParams, {
  disabled: true,
  immediate: false,
});

const loadData = async () => {
  loading.value = true;
  await refetch();
  loading.value = false;
  photos.value = unref(data)?.['hydra:member'] ?? [];
  if (props.openOnPhotoId) {
    currentIndex.value = Math.max(photos.value.findIndex((photo) => photo.id === props.openOnPhotoId), 0);
  } else {
    currentIndex.value = 0;
  }
};

watch(internalOpen, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    loadData();
  }
});

function closeModal() {
  internalOpen.value = false;
  photos.value = [];
  emit('close');
}

function previous() {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  } else {
    currentIndex.value = totalNumberOfPhotos.value - 1;
  }
}

function next() {
  if (currentIndex.value < totalNumberOfPhotos.value - 1) {
    currentIndex.value++;
  } else {
    currentIndex.value = 0;
  }
}
</script>

<style lang="scss" scoped></style>

